import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

export type SubscriptionPlan = "free_trial" | "paid" | "expired";

export interface SubscriptionContextValue {
  plan: SubscriptionPlan;
  isTrial: boolean;
  trialEndsAt: string | null;
  paidUntil: string | null;
  isExpired: boolean;
  daysRemaining: number;
  isLoading: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { profile, isLoading } = useAuth();

  const value = useMemo<SubscriptionContextValue>(() => {
    const sub = profile?.subscription;
    if (!sub) {
      return {
        plan: "free_trial",
        isTrial: true,
        trialEndsAt: null,
        paidUntil: null,
        isExpired: false,
        daysRemaining: 0,
        isLoading,
      };
    }

    let plan: SubscriptionPlan = "paid";
    if (sub.isExpired) plan = "expired";
    else if (sub.isTrial) plan = "free_trial";

    return {
      plan,
      isTrial: sub.isTrial,
      trialEndsAt: sub.trialEnds,
      paidUntil: sub.paidUntil,
      isExpired: sub.isExpired,
      daysRemaining: sub.daysRemaining,
      isLoading,
    };
  }, [profile, isLoading]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return ctx;
}
